import { ContainedBtn } from '@components/Buttons';
import { Resource, ResourceTopic } from '@core';
import { mc, StyleProps } from '@styles';
import { StaticImage } from 'gatsby-plugin-image';
import React, { FC } from 'react';

interface Props extends StyleProps {
  data: Resource;
  highlightedTopics?: ResourceTopic[];
  selectedTopics?: ResourceTopic[];
}

export const AffiliateInfoBox: FC<Props> = ({ style, className, data, highlightedTopics, selectedTopics }) => {
  const { merchantName, topics, webLink, webDescription, webImage } = data;

  const isTopicsHighlighted = (itm: ResourceTopic) => highlightedTopics?.some(highlightedTopic => highlightedTopic.id === itm.id);

  const getTopics = () => {
    const filteredTopics = !selectedTopics
      ? topics
      : topics.filter(itm => selectedTopics.some(selectedTopic => selectedTopic.id === itm.id));
    return filteredTopics.sort((a, b) => {
      if (isTopicsHighlighted(a) && !isTopicsHighlighted(b)) {
        return -1;
      }
      if (!isTopicsHighlighted(a) && isTopicsHighlighted(b)) {
        return 1;
      }
      return 0;
    });
  };

  const topicsToDisplay = getTopics();

  return (
    <div id={`merchant-${data.id}`} style={style} className={mc('flex flex-col w-full', 'sm:flex-row sm:items-start', className)}>
      <div className={mc('sm:mr-7 w-full sm:w-[324px]', 'flex flex-col items-center')}>
        {webImage ? (
          <div
            className={mc(
              'w-[350px] h-[210px] sm:w-[324px] sm:h-[195px]',
              'bg-no-repeat bg-center bg-contain',
              'border overflow-hidden rounded-xl',
            )}
            style={{ backgroundImage: `url(${webImage.url})` }}
          />
        ) : (
          <div
            className={mc(
              'w-[350px] h-[210px] sm:w-[324px] sm:h-[195px]',
              'border border-wireframes-300 overflow-hidden rounded-xl',
            )}
          >
            <StaticImage
              className="w-[350px] h-[210px] sm:w-[324px] sm:h-[195px]"
              src="./assets/imagePlaceholder.jpg"
              alt={merchantName}
              placeholder="blurred"
            />
          </div>
        )}
      </div>
      <div className="mt-4 sm:mt-0 sm:flex-1">
        {!!topicsToDisplay.length && (
          <div className="flex flex-row flex-wrap justify-start">
            {topicsToDisplay.map(itm => (
              <div
                key={itm.id}
                className={mc(
                  'mr-2 mb-2 px-4 py-1.5',
                  !isTopicsHighlighted(itm) ? 'bg-seafoam text-ocean-blue' : 'bg-salmon text-white',
                  'rounded-full',
                  'text-sm font-opensans leading-normal text-center font-semibold',
                )}
              >
                {itm.name}
              </div>
            ))}
          </div>
        )}
        {webLink ? (
          <a className="hover:opacity-70" href={webLink} target="_blank">
            <h4 className={mc('m-0', 'text-xl font-raleway leading-normal font-bold text-ocean-blue')}>{merchantName}</h4>
          </a>
        ) : (
          <h4 className={mc('m-0', 'text-xl font-raleway leading-normal font-bold text-ocean-blue')}>{merchantName}</h4>
        )}
        <div className="font-raleway leading-normal font-medium text-greyish-brown space-y-4">{webDescription}</div>
        <div className="flex flex-row-reverse">
          <ContainedBtn href={webLink} target="__blank" className="mt-3" color="orange" size="sm">
            {'Learn More'}
          </ContainedBtn>
        </div>
      </div>
    </div>
  );
};

export type AffiliateInfoBoxProps = Props;
export default AffiliateInfoBox;
